// File Location: src/services/cacheService.js

/**
 * Cache management service for clearing various types of cached data
 */

export const cacheService = {
  /**
   * Clear all localStorage data related to the application
   */
  clearLocalStorage: () => {
    try {
      const localStorageKeys = Object.keys(localStorage);
      const appSpecificKeys = localStorageKeys.filter(key => 
        key.includes('newPatientApplication') || 
        key.includes('policy_review_') || 
        key.includes('financial_review_') ||
        key.includes('samriddhihealthcare') ||
        key.includes('firebase') ||
        key.includes('notification') ||
        key.includes('dispute') ||
        key.includes('application') ||
        key.includes('user') ||
        key.includes('settings') ||
        key.includes('form_') ||
        key.includes('draft_') ||
        key.includes('temp_')
      );
      
      appSpecificKeys.forEach(key => {
        localStorage.removeItem(key);
      });
      
      console.log(`Cleared ${appSpecificKeys.length} localStorage items`);
      return true;
    } catch (error) {
      console.error('Error clearing localStorage:', error);
      return false;
    }
  },

  /**
   * Clear all sessionStorage data
   */
  clearSessionStorage: () => {
    try {
      sessionStorage.clear();
      console.log('Cleared sessionStorage');
      return true;
    } catch (error) {
      console.error('Error clearing sessionStorage:', error);
      return false;
    }
  },

  /**
   * Clear browser cache (if supported)
   */
  clearBrowserCache: async () => {
    try {
      if ('caches' in window) {
        const cacheNames = await caches.keys();
        await Promise.all(
          cacheNames.map(cacheName => caches.delete(cacheName))
        );
        console.log(`Cleared ${cacheNames.length} browser caches`);
        return true;
      } else {
        console.log('Browser cache API not supported');
        return false;
      }
    } catch (error) {
      console.error('Error clearing browser cache:', error);
      return false;
    }
  },

  /**
   * Clear specific form data from localStorage
   */
  clearFormData: (formKey) => {
    try {
      localStorage.removeItem(formKey);
      console.log(`Cleared form data: ${formKey}`);
      return true;
    } catch (error) {
      console.error(`Error clearing form data ${formKey}:`, error);
      return false;
    }
  },

  /**
   * Clear all application-related cached data
   */
  clearAllCache: async () => {
    try {
      const results = await Promise.allSettled([
        Promise.resolve(cacheService.clearLocalStorage()),
        Promise.resolve(cacheService.clearSessionStorage()),
        cacheService.clearBrowserCache()
      ]);

      const successCount = results.filter(result => 
        result.status === 'fulfilled' && result.value === true
      ).length;

      console.log(`Cache clearing completed: ${successCount}/3 operations successful`);
      return successCount === 3;
    } catch (error) {
      console.error('Error during cache clearing:', error);
      return false;
    }
  },

  /**
   * Get cache statistics for debugging
   */
  getCacheStats: () => {
    try {
      const localStorageCount = Object.keys(localStorage).length;
      const sessionStorageCount = Object.keys(sessionStorage).length;
      
      const appSpecificLocalStorage = Object.keys(localStorage).filter(key => 
        key.includes('newPatientApplication') || 
        key.includes('policy_review_') || 
        key.includes('financial_review_') ||
        key.includes('samriddhihealthcare') ||
        key.includes('firebase') ||
        key.includes('notification') ||
        key.includes('dispute') ||
        key.includes('application') ||
        key.includes('user') ||
        key.includes('settings')
      );

      return {
        localStorage: {
          total: localStorageCount,
          appSpecific: appSpecificLocalStorage.length,
          keys: appSpecificLocalStorage
        },
        sessionStorage: {
          total: sessionStorageCount
        },
        browserCacheSupported: 'caches' in window
      };
    } catch (error) {
      console.error('Error getting cache stats:', error);
      return null;
    }
  }
};

export default cacheService;
